import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon  from '@mui/icons-material/Check';
import CloseIcon  from '@mui/icons-material/Close';
import { approveApproval, rejectApproval, getApprovalById } from '../../api/approvalApi';
import { useToast }    from '../../context/ToastContext';
import { useApproval } from '../../context/ApprovalContext';
import RejectModal           from './RejectModal';
import ApprovalStatusBadge   from './ApprovalStatusBadge';
import ActionTypeChip        from './ActionTypeChip';
import DiffTable             from './DiffTable';
import ContentComparisonView from './ContentComparisonView';
import ContentDetailPanel    from './detail/ContentDetailPanel';

function DetailRow({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography>{value || '—'}</Typography>
    </Box>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(dateStr));
}

function parseSnapshot(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

export default function ContentPreviewDrawer({
  open,
  approvalRecord,
  onClose,
  onDecided,
  readOnly = false,
  onEdit,
}) {
  const [rejectOpen,        setRejectOpen]        = useState(false);
  const [compareOpen,       setCompareOpen]        = useState(false);
  const [loading,           setLoading]            = useState(false);
  const [currentSnapshot,   setCurrentSnapshot]    = useState(null);
  const [proposedSnapshot,  setProposedSnapshot]   = useState(null);
  const [proposedData,      setProposedData]       = useState(null);
  const [fullDetail,        setFullDetail]         = useState(null);

  const { toast }          = useToast();
  const { recordDecision } = useApproval();

  useEffect(() => {
    if (!open || !approvalRecord) return;

    setCurrentSnapshot(null);
    setProposedSnapshot(null);
    setProposedData(null);
    setCompareOpen(false);
    setFullDetail(null);

    getApprovalById(approvalRecord.approvalId)
      .then((full) => {
        setCurrentSnapshot(parseSnapshot(full?.currentSnapshot));
        setProposedSnapshot(parseSnapshot(full?.proposedSnapshot));
        setProposedData(full?.proposedData ?? null);
        setFullDetail(full);
      })
      .catch(() => {
        setCurrentSnapshot(null);
        setProposedSnapshot(null);
        setProposedData(null);
        setFullDetail(null);
      });
  }, [open, approvalRecord?.approvalId, approvalRecord?.contentType, approvalRecord?.actionType]);

  useEffect(() => {
    if (!open) {
      setCompareOpen(false);
      setCurrentSnapshot(null);
      setProposedSnapshot(null);
      setProposedData(null);
      setFullDetail(null);
    }
  }, [open]);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await approveApproval(approvalRecord.approvalId);
      recordDecision(approvalRecord.approvalId);
      toast.success(`${approvalRecord.sectionLabel} approved!`);
      setCompareOpen(false);
      onDecided?.();
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (reason) => {
    setLoading(true);
    try {
      await rejectApproval(approvalRecord.approvalId, reason);
      recordDecision(approvalRecord.approvalId);
      toast.success(`${approvalRecord.sectionLabel} rejected.`);
      setRejectOpen(false);
      setCompareOpen(false);
      onDecided?.();
    } finally {
      setLoading(false);
    }
  };

  const actionType  = fullDetail?.actionType ?? approvalRecord?.actionType;
  const contentType = approvalRecord?.contentType;
  const hasProposed = !!proposedSnapshot;
  const hasCurrent  = !!currentSnapshot;

  // In readOnly mode the compare button is hidden — user sees diff table only.
  // 'convert' also uses the full ContentComparisonView (not a diff — two different content
  // types side by side) since the drawer itself is too narrow for that layout.
  const canCompare = !readOnly && (actionType === 'edit' || actionType === 'convert') && (hasCurrent || hasProposed);

  const previewData = actionType === 'create' ? proposedSnapshot
    : actionType === 'delete'                 ? currentSnapshot
    : null;

  // Enrich display values from the full API response when available
  const displayStatus    = fullDetail?.status ?? approvalRecord?.status ?? 'pending';
  const rejectionReason  = fullDetail?.rejectionReason ?? null;
  const submittedByName  = approvalRecord?.submittedByName ?? '';
  const submittedAt      = fullDetail?.submittedAt ?? approvalRecord?.submittedAt ?? null;

  // In read-only mode (card-side "View" button), bypass the drawer and open
  // ContentComparisonView directly so users see the same full comparison as approvers.
  if (readOnly) {
    return (
      <ContentComparisonView
        open={open}
        onClose={onClose}
        contentType={contentType}
        sectionLabel={approvalRecord?.sectionLabel}
        contentName={approvalRecord?.contentName}
        actionType={actionType}
        currentSnapshot={currentSnapshot}
        proposedSnapshot={proposedSnapshot}
        proposedData={proposedData}
        readOnly={true}
        rejectionReason={rejectionReason}
        isRejected={displayStatus === 'rejected'}
        onEdit={onEdit}
      />
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100vw', sm: 480 }, p: 3, display: 'flex', flexDirection: 'column' },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">
            {readOnly ? 'View Proposal' : 'Review Content'}
          </Typography>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>Close</Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2} flexGrow={1} overflow="auto">
          {/* Rejection reason banner — readOnly view of a rejected proposal */}
          {readOnly && rejectionReason && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} mb={0.25}>Rejection Reason</Typography>
              <Typography variant="body2">{rejectionReason}</Typography>
            </Alert>
          )}

          <DetailRow label="Section"      value={approvalRecord.sectionLabel} />
          <DetailRow label="Content Name" value={approvalRecord.contentName} />
          <DetailRow label="Submitted By" value={submittedByName} />
          <DetailRow label="Submitted"    value={formatDate(submittedAt)} />

          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box mt={0.5} display="flex" alignItems="center" gap={1}>
              <ApprovalStatusBadge status={displayStatus} actionType={actionType} />
              <ActionTypeChip actionType={actionType} />
            </Box>
          </Box>

          {/* ── CREATE: full proposed panel ──────────────────────────────── */}
          {actionType === 'create' && previewData && (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}
              >
                New Content Preview
              </Typography>
              <Box
                sx={{
                  mt: 0.75,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'white',
                }}
              >
                <ContentDetailPanel
                  contentType={contentType}
                  data={previewData}
                  diffKeys={new Set()}
                  side="proposed"
                />
              </Box>
            </Box>
          )}

          {/* ── DELETE: current content panel + warning ───────────────────── */}
          {actionType === 'delete' && (
            <>
              <Alert severity="error" icon={false} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={700} mb={0.5}>
                  Permanent Deletion Requested
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Approving this will permanently delete{' '}
                  <strong>{approvalRecord.contentName || 'this content'}</strong>.
                  This action cannot be undone.
                </Typography>
              </Alert>
              {previewData && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}
                  >
                    Content Being Deleted
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      border: '1px solid #fca5a5',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'white',
                    }}
                  >
                    <ContentDetailPanel
                      contentType={contentType}
                      data={previewData}
                      diffKeys={new Set()}
                      side="current"
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* ── CONVERT: info banner + link to the full side-by-side view ──── */}
          {/* Not a diff (buildComparisonRows assumes one shape on both sides) — the drawer is
              too narrow for the real two-panel layout, so it lives in ContentComparisonView. */}
          {actionType === 'convert' && (
            <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={700} mb={0.5}>
                Convert to Another Type
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={canCompare ? 1 : 0}>
                Approving <strong>{approvalRecord.contentName || 'this item'}</strong> will delete the
                original and create a new item of the target type in Draft. This cannot be undone.
              </Typography>
              {canCompare && (
                <Button size="small" variant="outlined" onClick={() => setCompareOpen(true)} sx={{ mt: 0.5 }}>
                  Compare — what's deleted vs. created
                </Button>
              )}
            </Alert>
          )}

          {/* ── EDIT: compact diff table ──────────────────────────────────── */}
          {actionType === 'edit' && (
            <DiffTable
              contentType={contentType}
              proposedData={proposedSnapshot ? JSON.stringify(proposedSnapshot) : proposedData}
              liveContent={currentSnapshot}
              onCompare={canCompare ? () => setCompareOpen(true) : undefined}
            />
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {readOnly ? (
            <Button variant="outlined" onClick={onClose} startIcon={<CloseIcon />}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => setRejectOpen(true)}
                disabled={loading}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={handleApprove}
                disabled={loading}
              >
                {loading ? 'Approving...' : 'Approve'}
              </Button>
            </>
          )}
        </Stack>
      </Drawer>

      {/* ── Reject reason modal — only used in review mode ──────────────── */}
      {!readOnly && (
        <RejectModal
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          onConfirm={handleReject}
        />
      )}

      {/* ── Full side-by-side comparison dialog — only in review mode ────── */}
      {!readOnly && (
        <ContentComparisonView
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          contentType={contentType}
          sectionLabel={approvalRecord.sectionLabel}
          contentName={approvalRecord.contentName}
          actionType={actionType}
          currentSnapshot={currentSnapshot}
          proposedSnapshot={proposedSnapshot}
          proposedData={proposedData}
          onApprove={handleApprove}
          onReject={() => { setCompareOpen(false); setRejectOpen(true); }}
          loading={loading}
        />
      )}
    </>
  );
}
